import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { LayoutTranslationFn } from '@typings/layout.ts';
import { addDialog, removeDialog } from '@ui/modals/dialogOpenState.ts';
import { withFocusTrap } from '@ui/modals/FocusTrap.tsx';
import { CloseButton, BackButton } from '@ui/modals/ModalControls.tsx';

import { listenToEscape } from '../../../plugins/utils/keyboardUtils.ts';
import useComponentFocused from '../../hooks/useComponentFocused.tsx';
import { useCSSColors } from '../../hooks/useCSSColors.ts';

type ModalBoxProps = {
  $fullScreenMobile?: boolean;
  width?: string;
};

type ModalWindowInjectedProps = {
  setModalBackButtonAction: (clickAction: () => void) => void;
  setModalDismissAction: (dismissAction: () => void) => void;
  /** Shows the title in the header row, beside the close button. */
  setModalTitle: (title: React.ReactNode) => void;
};

type ModalWindowBaseProps = {
  T: LayoutTranslationFn;
  width?: string;
};

const BackgroundMask = styled.div`
  z-index: 999;
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
  display: grid;
  position: absolute;
  inset-inline-start: 0;
  top: 0;
`;

const ModalBox = styled.div<ModalBoxProps>`
  width: ${({ width }) => width || 'auto'};
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
  background: var(--color-background);
  justify-self: center;
  align-self: center;
  position: relative;
  height: auto;

  display: flex;
  flex-direction: column;
  overflow: auto;

  border-radius: ${({ theme }) => theme.standardRadius};
  ${({ theme }) => (typeof theme.boxShadow === 'function' ? theme.boxShadow() : '')};

  .-desktop & {
    min-height: 200px;
    min-width: 240px;
    max-width: 74%;
  }

  ${({ $fullScreenMobile }) =>
    $fullScreenMobile &&
    `
    .-mobile & {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      border-radius: 0;
    }
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 0 auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 14px 4px;
  width: 100%;

  .-desktop & {
    position: relative;
    padding: 24px 16px 4px;
  }
`;

const ModalContent = styled.div`
  padding: 0 12px 16px;

  .-desktop & {
    padding: 0 20px 16px;
  }
`;

// Offsets line the title up with the content padding.
const ModalTitle = styled.div`
  align-self: center;
  min-width: 0;
  margin-inline-start: -2px;

  .-desktop & {
    margin-inline-start: 4px;
  }
`;

const withModalWindow = <P extends object>(
  WrappedComponent: React.ComponentType<P & ModalWindowInjectedProps>,
  focusFirst?: string,
  { fullScreenMobile = false }: { fullScreenMobile?: boolean } = {},
) => {
  const ModalWindow = (props: P & ModalWindowBaseProps) => {
    const dialog = useRef({}).current;
    const maskRef = useRef<HTMLDivElement>(null);
    // Only a click on this modal's own backdrop dismisses it, so clicking a toast does not.
    const { ref, isComponentFocused, setIsComponentFocused } = useComponentFocused(true, event =>
      Boolean(maskRef.current?.contains(event.target as Node)),
    );
    const [backButtonAction, setBackButtonAction] = useState<(() => void) | null>(null);
    const [onModalDismissAction, setOnModalDismissAction] = useState<(() => void) | null>(null);
    const [title, setTitle] = useState<React.ReactNode>(null);
    const colors = useCSSColors();

    const { T, width } = props;

    // A toast reads this to know a dialog is open and keep its hands off the focus.
    useEffect(() => {
      if (!isComponentFocused) {
        return;
      }
      addDialog(dialog);
      return () => removeDialog(dialog);
    }, [isComponentFocused]);

    useEffect(() => {
      // if the outside click was detected hide the modal and call side effects
      if (!isComponentFocused && typeof onModalDismissAction === 'function') {
        // check if onModalDismissAction was initialized before calling it
        onModalDismissAction();
      }
    }, [isComponentFocused]);

    const setBackButton = (clickAction: () => void) => {
      setBackButtonAction(() => clickAction);
    };

    const setModalDismissAction = (dismissAction: () => void) => {
      setOnModalDismissAction(() => dismissAction);
    };

    // Listen on the document so Escape works wherever the focus happens to sit.
    useEffect(() => {
      const removeEscapeListener = listenToEscape(e => {
        setIsComponentFocused(false);
        e.preventDefault();
      }, document);
      return removeEscapeListener;
    }, []);
    if (!isComponentFocused) {
      return null;
    } // do not render modal if it was unfocused

    return (
      <BackgroundMask ref={maskRef}>
        <ModalBox
          ref={ref as React.RefObject<HTMLDivElement>}
          width={width}
          $fullScreenMobile={fullScreenMobile}
          data-testid="ModalBox"
          role="dialog"
          id="ModalBox"
          aria-modal={true}
          aria-labelledby="ModalBoxTitle"
        >
          <ModalHeader>
            {!!backButtonAction && (
              <BackButton onClick={backButtonAction} text={T('ui:Back')} color={colors.secondary} />
            )}
            {title && <ModalTitle>{title}</ModalTitle>}
            <CloseButton
              onClick={() => setIsComponentFocused(false)}
              tooltipText={T('ui:Close')}
              color={colors.secondary}
            />
          </ModalHeader>
          <ContentWrapper>
            <ModalContent>
              <WrappedComponent
                {...props}
                setModalBackButtonAction={setBackButton}
                setModalDismissAction={setModalDismissAction}
                setModalTitle={setTitle}
              />
            </ModalContent>
          </ContentWrapper>
        </ModalBox>
      </BackgroundMask>
    );
  };

  return withFocusTrap(ModalWindow, 'ModalBox', focusFirst);
};

export { withModalWindow };
